import { injectable } from 'tsyringe';
import { authenticatedFetch } from '@/shared/auth/AuthenticatedFetch';
import type {
    EffectiveNotificationPreference,
    InAppNotification,
    JobRunPage,
    NotificationActivityPage,
    NotificationPreferencePatch,
    PreferenceUpdateResult,
} from './NotificationTypes';

const BASE_PATH = '/_notifications';

/**
 * Client for the notifications service's self-service surface — the bell tray
 * and the user's channel preferences.
 *
 * Hand-written against `authenticatedFetch` rather than a generated client:
 * `@bmai/notifications-api-client` is not consumed here yet, so this is the
 * working path today. Swapping to the generated client later is contained to
 * this file.
 */
@injectable()
export class NotificationsApiService {
    async listNotifications(limit?: number): Promise<InAppNotification[]> {
        const query = limit === undefined ? '' : `?limit=${limit}`;
        return this.request<InAppNotification[]>(`/api/me/notifications${query}`);
    }

    async getUnreadCount(): Promise<number> {
        const result = await this.request<{ count: number }>('/api/me/notifications/unread-count');
        return result.count;
    }

    async markRead(notificationId: string): Promise<InAppNotification> {
        return this.request<InAppNotification>(`/api/me/notifications/${notificationId}/read`, {
            method: 'PATCH',
        });
    }

    async markAllRead(): Promise<number> {
        const result = await this.request<{ updated: number }>('/api/me/notifications/mark-all-read', {
            method: 'PATCH',
        });
        return result.updated;
    }

    /**
     * Tenant-wide send history. Unlike the tray, this reports across every
     * recipient, so it requires the `notifications.view` permission — a
     * caller without it gets a 403 rather than an empty list.
     */
    async getActivity(limit?: number): Promise<NotificationActivityPage> {
        const query = limit === undefined ? '' : `?limit=${limit}`;
        return this.request<NotificationActivityPage>(`/api/notifications/activity${query}`);
    }

    /**
     * The signed-in user's own background jobs (syncs and other long-running
     * work they started), most recent first — the Jobs tab of the tray.
     */
    async listMyJobs(limit?: number): Promise<JobRunPage> {
        const query = limit === undefined ? '' : `?limit=${limit}`;
        return this.request<JobRunPage>(`/api/me/jobs${query}`);
    }

    async getPreferences(): Promise<EffectiveNotificationPreference[]> {
        return this.request<EffectiveNotificationPreference[]>('/api/me/notification-preferences');
    }

    /**
     * Update one notification type's channels. A policy rejection comes back as
     * `valid: false` with issues — it is not an HTTP error, so callers must check
     * `valid` rather than assuming a resolved promise means the change stuck.
     */
    async updatePreference(
        templateType: string,
        patch: NotificationPreferencePatch,
    ): Promise<PreferenceUpdateResult> {
        return this.request<PreferenceUpdateResult>(
            `/api/me/notification-preferences/${encodeURIComponent(templateType)}`,
            { method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify(patch) },
        );
    }

    private async request<T>(path: string, init?: RequestInit): Promise<T> {
        const response = await authenticatedFetch(`${BASE_PATH}${path}`, init);
        if (!response.ok) {
            throw new Error(`Notifications request failed (${response.status}) for ${path}`);
        }
        return (await response.json()) as T;
    }
}
